<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div class="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 class="text-lg font-bold text-neutral-900">用户管理</h2>
          <p class="text-sm text-neutral-500 mt-1">
            默认只显示审核通过的正常用户；待审核 / 已驳回账号请切换筛选查看，并在「注册审核」里处理
          </p>
        </div>
        <div class="flex gap-3 flex-wrap">
          <select
            v-model.number="statusFilter"
            @change="handleStatusChange"
            class="px-3 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            title="按审核状态筛选"
          >
            <option :value="1">正常用户</option>
            <option :value="0">待审核</option>
            <option :value="2">已驳回</option>
            <option :value="-1">全部账号</option>
          </select>
          <div class="relative">
            <input
              type="text"
              v-model="searchKey"
              @input="handleSearch"
              placeholder="搜索昵称/用户名/手机号..."
              class="px-4 py-2 pl-10 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm w-64"
            />
            <i class="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"></i>
          </div>
          <button
            @click="openImport"
            class="px-3 py-2 rounded-lg bg-secondary text-white hover:bg-secondary/90 transition-colors text-sm flex items-center gap-1"
          >
            <i class="fa fa-upload"></i>批量导入
          </button>
          <button
            @click="exportUsers"
            :disabled="exporting"
            class="px-3 py-2 rounded-lg border border-neutral-300 hover:border-primary hover:text-primary transition-colors text-sm flex items-center gap-1 disabled:opacity-50"
          >
            <i class="fa fa-download" :class="{ 'fa-spin': exporting }"></i>导出CSV
          </button>
          <button
            @click="getList"
            class="px-3 py-2 rounded-lg border border-neutral-300 hover:border-primary hover:text-primary transition-colors"
            title="刷新列表"
          >
            <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- 用户统计表格 -->
      <div v-loading="loading" class="overflow-x-auto" style="min-height: 120px">
        <table class="w-full text-sm" v-if="userList.length > 0">
          <thead>
            <tr class="text-left text-neutral-500 border-b border-neutral-200">
              <th class="py-3 px-3 font-medium">用户</th>
              <th class="py-3 px-3 font-medium">学校/学科</th>
              <th class="py-3 px-3 font-medium text-center">创值</th>
              <th class="py-3 px-3 font-medium text-center">评论</th>
              <th class="py-3 px-3 font-medium text-center">需求</th>
              <th class="py-3 px-3 font-medium text-center">工具</th>
              <th class="py-3 px-3 font-medium">活跃度</th>
              <th class="py-3 px-3 font-medium">注册时间</th>
              <th class="py-3 px-3 font-medium text-center">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="u in userList"
              :key="u.uid"
              class="border-b border-neutral-100 hover:bg-neutral-50"
            >
              <td class="py-3 px-3">
                <div class="flex items-center gap-3">
                  <img
                    v-if="u.avatar"
                    :src="u.avatar"
                    class="w-9 h-9 rounded-full object-cover border border-neutral-200"
                    alt=""
                  />
                  <div
                    v-else
                    class="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold"
                  >
                    {{ (u.name || '?').charAt(0) }}
                  </div>
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="font-bold text-neutral-900">{{ u.name }}</span>
                      <span
                        class="text-xs px-2 py-0.5 rounded-full"
                        :class="{
                          'bg-purple-100 text-purple-700': u.adminPerms === '*',
                          'bg-blue-100 text-blue-700': u.utype === 'admin' && u.adminPerms !== '*',
                        }"
                        v-if="u.utype === 'admin'"
                      >
                        {{ u.adminPerms === '*' ? '超级管理员' : '管理员' }}
                      </span>
                      <span
                        v-if="u.status === 0"
                        class="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
                      >
                        待审核
                      </span>
                      <span
                        v-else-if="u.status === 2"
                        class="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600"
                      >
                        已驳回
                      </span>
                    </div>
                    <div class="text-xs text-neutral-400">
                      {{ u.loginId || '-' }}<span v-if="u.phone"> · {{ u.phone }}</span>
                    </div>
                  </div>
                </div>
              </td>
              <td class="py-3 px-3 text-neutral-600">
                <div class="truncate max-w-[180px]">{{ u.schName || '-' }}</div>
                <div class="text-xs text-neutral-400">{{ u.subject || '-' }}<span v-if="u.stage"> · {{ u.stage }}</span></div>
              </td>
              <td class="py-3 px-3 text-center">
                <span class="inline-flex items-center gap-1 text-amber-600 font-bold">
                  <i class="fa fa-circle-o text-xs"></i>{{ u.integral || 0 }}
                </span>
              </td>
              <td class="py-3 px-3 text-center text-neutral-700">{{ u.commentCount || 0 }}</td>
              <td class="py-3 px-3 text-center text-neutral-700">{{ u.demandCount || 0 }}</td>
              <td class="py-3 px-3 text-center text-neutral-700">{{ u.toolCount || 0 }}</td>
              <td class="py-3 px-3">
                <div class="flex items-center gap-2">
                  <span class="text-xs px-2 py-0.5 rounded-full" :class="activityClass(u)">
                    {{ activityText(u) }}
                  </span>
                  <span class="text-xs text-neutral-400">{{ onlineText(u.onlineMinutes) }}</span>
                </div>
              </td>
              <td class="py-3 px-3 text-neutral-400 text-xs whitespace-nowrap">{{ u.createTime }}</td>
              <td class="py-3 px-3 text-center">
                <button
                  v-if="u.utype !== 'admin'"
                  @click="resetPassword(u)"
                  class="px-3 py-1.5 rounded-lg border border-neutral-300 text-xs text-neutral-600 hover:border-primary hover:text-primary transition-colors"
                  title="重置该用户密码"
                >
                  <i class="fa fa-key mr-1"></i>重置密码
                </button>
                <span v-else class="text-xs text-neutral-300">—</span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- 空状态 -->
        <div v-if="!loading && userList.length === 0" class="text-center py-12 text-neutral-400">
          <i class="fa fa-users text-4xl mb-3"></i>
          <p>暂无用户</p>
        </div>
      </div>

      <!-- 分页 -->
      <div class="flex items-center justify-between pt-4" v-if="total > 0">
        <span class="text-sm text-neutral-500">共 {{ total }} 位用户</span>
        <div class="flex gap-2">
          <button
            :disabled="page <= 1"
            @click="changePage(page - 1)"
            class="px-3 py-1.5 rounded-lg border border-neutral-300 text-sm disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
          >
            上一页
          </button>
          <span class="px-3 py-1.5 text-sm text-neutral-600">{{ page }} / {{ totalPages }}</span>
          <button
            :disabled="page >= totalPages"
            @click="changePage(page + 1)"
            class="px-3 py-1.5 rounded-lg border border-neutral-300 text-sm disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
          >
            下一页
          </button>
        </div>
      </div>
    </div>

    <!-- 批量导入弹窗 -->
    <el-dialog v-model="importVisible" title="批量导入用户" width="600px">
      <div class="space-y-4">
        <div class="text-sm bg-neutral-50 rounded-lg p-3 text-neutral-600 leading-relaxed">
          <p class="font-bold mb-1">格式说明（每行一条，用逗号或 Tab 分隔）：</p>
          <p class="font-mono text-xs bg-white border border-neutral-200 rounded p-2 mt-1">
            姓名,用户名,密码,手机号,学校<br />
            张老师,zhang01,Abc@123,13800000001,某某小学<br />
            李老师,li02,Teacher@66,,某某中学
          </p>
          <ul class="text-xs text-neutral-400 mt-2 space-y-0.5 list-disc list-inside">
            <li>姓名、用户名为必填；用户名不可与已有用户重复</li>
            <li>密码可空（默认 Abc@123）；需为 6-15 位且包含数字/字母/特殊字符至少 2 种</li>
            <li>新用户初始角色为普通用户，并获得 5 创值</li>
          </ul>
        </div>
        <el-input
          v-model="importText"
          type="textarea"
          :rows="10"
          placeholder="在此粘贴用户数据，每行一条..."
        />
        <div v-if="importResult" class="text-sm rounded-lg p-3" :class="importResult.created > 0 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'">
          <p>
            导入完成：成功 <b>{{ importResult.created }}</b> 人，
            跳过 <b>{{ importResult.skipped }}</b> 人
          </p>
          <p v-if="importResult.reason" class="text-xs mt-1 text-neutral-500 break-all">
            原因：{{ importResult.reason }}
          </p>
        </div>
      </div>
      <template #footer>
        <button
          @click="importVisible = false"
          class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:border-neutral-400 transition-colors text-sm"
        >
          关闭
        </button>
        <button
          @click="submitImport"
          :disabled="importing"
          class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium ml-2 disabled:opacity-50"
        >
          {{ importing ? '导入中...' : '开始导入' }}
        </button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";

export default {
  name: "UserManager",
  data() {
    return {
      userList: [],
      total: 0,
      page: 1,
      pageSize: 15,
      searchKey: "",
      /** 审核状态筛选：1-正常（默认）0-待审核 2-已驳回 -1-全部 */
      statusFilter: 1,
      loading: false,
      exporting: false,
      importVisible: false,
      importing: false,
      importText: "",
      importResult: null,
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
  },
  created() {
    this.getList();
  },
  methods: {
    // ===== 重置用户密码 =====
    async resetPassword(u) {
      try {
        await ElMessageBox.confirm(
          `确认将「${u.name}」的密码重置为默认密码 Abc@123？\n重置后该用户会收到站内信通知。`,
          "重置密码",
          { confirmButtonText: "确认重置", cancelButtonText: "取消", type: "warning" }
        );
      } catch (e) {
        return;
      }
      try {
        const res = await this.$axios({
          url: "user/admin/reset/password",
          method: "POST",
          data: { data: await this.$aesEncrypt({ uid: u.uid }) },
        });
        if (res.code === 200) {
          ElMessage({ message: res.data.msg || "密码已重置", type: "success" });
        } else {
          ElMessage({ message: res.msg || "重置失败", type: "error" });
        }
      } catch (e) {
        ElMessage({ message: "重置失败", type: "error" });
      }
    },
    async getList() {
      this.loading = true;
      try {
        const data = await this.$aesEncrypt({
          page: this.page,
          pageSize: this.pageSize,
          searchKey: this.searchKey,
          status: this.statusFilter,
        });
        const res = await this.$axios({
          url: "user/admin/userlist",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          this.userList = res.data.list || [];
          this.total = res.data.total || 0;
        }
      } catch (e) {
        console.error("获取用户列表失败:", e);
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.page = 1;
        this.getList();
      }, 400);
    },
    handleStatusChange() {
      this.page = 1;
      this.getList();
    },
    changePage(p) {
      this.page = p;
      this.getList();
    },
    /** 活跃度等级：按累计在线时长 */
    activityText(u) {
      const m = u.onlineMinutes || 0;
      if (m >= 300) return "活跃";
      if (m >= 60) return "一般";
      return "低";
    },
    activityClass(u) {
      const m = u.onlineMinutes || 0;
      if (m >= 300) return "bg-green-100 text-green-700";
      if (m >= 60) return "bg-blue-100 text-blue-700";
      return "bg-neutral-100 text-neutral-500";
    },
    onlineText(minutes) {
      const m = minutes || 0;
      if (m < 60) return m + " 分钟";
      return Math.floor(m / 60) + " 小时 " + (m % 60) + " 分";
    },
    openImport() {
      this.importResult = null;
      this.importVisible = true;
    },
    async submitImport() {
      if (!this.importText.trim()) {
        ElMessage.warning("请先粘贴要导入的用户数据");
        return;
      }
      this.importing = true;
      try {
        const data = await this.$aesEncrypt({ text: this.importText });
        const res = await this.$axios({
          url: "user/admin/batchimport",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          this.importResult = res.data;
          ElMessage.success("导入完成");
          this.getList();
        } else {
          ElMessage.error(res.msg || "导入失败");
        }
      } catch (e) {
        ElMessage.error("导入失败，请稍后重试");
      } finally {
        this.importing = false;
      }
    },
    /** 导出 CSV（fetch 带 token 下载流） */
    async exportUsers() {
      this.exporting = true;
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(process.env.VUE_APP_API_ROOT + "/user/admin/export", {
          headers: { Authorization: "Bearer " + token },
        });
        if (!res.ok) {
          throw new Error("下载失败");
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "用户列表_" + new Date().toISOString().slice(0, 10) + ".csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        ElMessage.success("导出成功");
      } catch (e) {
        ElMessage.error("导出失败，请稍后重试");
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
